'use client';
import Link from 'next/link';
import Image from 'next/image';
import { UserCircleIcon } from '@heroicons/react/24/solid';
import { useAuthUserContext } from '@/contexts/authUserContext';
import { signOutUser } from '@/lib/client_auth';
import { useEffect } from 'react';

interface UserProfileProps {}

const UserProfile: React.FC<UserProfileProps> = () => {
  const { user, isAuthenticated } = useAuthUserContext();

  // Close dropdown on click outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      const details = document.querySelector('details.group');
      if (details && !details.contains(event.target as Node)) {
        details.removeAttribute('open');
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  return (
    <details className='relative group z-300' tabIndex={0}>
      <summary
        className='flex items-center list-none focus:outline-none cursor-pointer'
        aria-label='User menu'
      >
        {isAuthenticated && user ? (
          <Image
            width={24}
            height={24}
            src={user.photoURL as string}
            alt='User'
            className='h-6 w-6 rounded-full object-cover'
          />
        ) : (
          <UserCircleIcon className='h-6 w-6 text-black hover:text-gray-800' />
        )}
      </summary>
      <div
        className='absolute right-0 mt-2 w-40 bg-white border-light-gray rounded shadow-lg z-50'
        // closes dropdown on click
        onClick={(e) => {
          // closes the <details> when a menu item is clicked
          (e.target as HTMLElement).closest('details')?.removeAttribute('open');
        }}
      >
        {isAuthenticated && user ? (
          <div className='flex flex-col'>
            <div className='px-4 py-2 border-b border-light-gray'>
              <p className='text-sm font-semibold text-gray-800'>
                {user.displayName || 'User'}
              </p>
            </div>
            <a
              href={`/user/profile/${user.uid}`}
              className="px-4 py-2 hover:bg-gray-100 text-sm"
              target="_blank"
            >
              View Profile
            </a>
            <button
              className='px-4 py-2 hover:bg-gray-100 text-sm'
              onClick={signOutUser}
            >
              Logout
            </button>
          </div>
        ) : (
          <Link
            href='/auth/login'
            className='block px-4 py-2 hover:bg-gray-100 text-sm'
            prefetch={false}
          >
            Login
          </Link>
        )}
      </div>
    </details>
  );
};

export default UserProfile;
